/**
 * Manages per-step form refs for the wizard.
 * Each step gets a stable RefObject keyed by step.key so useStepValidation
 * can call validate() / syncToPayload() on each form imperatively.
 */
import { useCallback, useMemo, useRef } from 'react';
import type { RefObject } from 'react';
import type { StepFormApi } from '@/hooks/useStepValidation';
import type { StepConfig } from '../config/schedule.types';

interface UseStepRefsResult {
  stepRefsMap: React.MutableRefObject<
    Record<string, RefObject<StepFormApi | null>>
  >;
  getStepRef: (_stepKey: string) => RefObject<StepFormApi | null>;
  stepRefs: Record<string, RefObject<StepFormApi | null>>;
}

export function useStepRefs(steps: StepConfig[]): UseStepRefsResult {
  const stepRefsMap = useRef<Record<string, RefObject<StepFormApi | null>>>({});

  const getStepRef = useCallback(
    (stepKey: string): RefObject<StepFormApi | null> => {
      if (!stepRefsMap.current[stepKey]) {
        stepRefsMap.current[stepKey] = { current: null };
      }
      return stepRefsMap.current[stepKey];
    },
    []
  );

  const stepRefs = useMemo(() => {
    const refs: Record<string, RefObject<StepFormApi | null>> = {};
    for (const step of steps) {
      refs[step.key] = getStepRef(step.key);
    }
    return refs;
  }, [steps, getStepRef]);

  return { stepRefsMap, getStepRef, stepRefs };
}
